import useSWR from 'swr';

// Define a generic type for the fetcher that specifies the return type
type Fetcher<T> = (props: { startDate: string; endDate: string }) => Promise<Record<string, T[]>>;
const getRandomRefreshInterval = () => {
  const min = 20000;
  const max = 40000;
  const step = 100;
  return Math.floor(Math.random() * ((max - min) / step + 1)) * step + min;
};
type UseItemsProps<T> = {
  startDate: string;
  endDate: string;
  fetcher: Fetcher<T>;
  opId: string;
  refreshInterval?: number;
};

interface Item {
  date?: string;
  id: string;
}

export const useItems = <T extends Item>({ startDate, endDate, fetcher, opId, refreshInterval }: UseItemsProps<T>) => {
  const refreshIntervalDefault = refreshInterval ?? getRandomRefreshInterval();
  const { data, error, isLoading, mutate } = useSWR({ startDate, endDate, opId }, fetcher, {
    refreshInterval: refreshIntervalDefault,
  });

  const deleteItem = (itemToDelete: T) => {
    const updatedItems = { ...data };
    const { date } = itemToDelete;
    if (!date) {
      console.error(`${opId} deleteItem has no date`);
      return;
    }
    if (updatedItems[date]) {
      updatedItems[date] = updatedItems[date].filter((item: T) => item.id !== itemToDelete.id);
      void mutate(updatedItems);
    }
  };
  const deleteItems = (itemsToDelete: T[]) => {
    const updatedItems = { ...data };

    itemsToDelete.forEach((itemToDelete) => {
      const { date } = itemToDelete;
      if (!date) {
        console.error(`${opId} deleteItem has no date`);
        return;
      }
      if (updatedItems[date]) {
        updatedItems[date] = updatedItems[date].filter((item: T) => item.id !== itemToDelete.id);
      }
    });
    void mutate(updatedItems);
  };
  const addItem = async (itemToAdd: T) => {
    const updatedItems = { ...data };
    const { date } = itemToAdd;
    if (!data) {
      return;
    }

    if (!date) {
      console.error(`${opId} addItem has no date`);
      return;
    }
    if (data[date]) {
      data[date].push(itemToAdd);
    } else {
      data[date] = [itemToAdd];
    }
    await mutate(updatedItems);
  };
  const addItems = (itemsToAdd: T[]) => {
    const updatedItems = { ...data };

    itemsToAdd.forEach((itemToAdd) => {
      const { date } = itemToAdd;
      if (!data) {
        return;
      }

      if (!date) {
        console.error(`${opId} addItems has no date`);
        return;
      }
      if (updatedItems[date]) {
        updatedItems[date].push(itemToAdd);
      } else {
        updatedItems[date] = [itemToAdd];
      }
    });

    void mutate(updatedItems);
  };
  const updateItem = (itemToUpdate: T, revalidate = true) => {
    const updatedItems = { ...data };
    const { date } = itemToUpdate;
    if (!data) {
      return;
    }

    if (!date) {
      console.error(`${opId} updateItem has no date`);
      return;
    }
    updatedItems[date] = (updatedItems[date] || []).map((item) => (item.id === itemToUpdate.id ? itemToUpdate : item));
    void mutate(updatedItems, { revalidate });
  };
  const reFetch = () => {
    return mutate();
  };
  return {
    items: data || {},
    isLoading,
    error,
    deleteItem,
    deleteItems,
    addItem,
    updateItem,
    addItems,
    reFetch,
  };
};
